import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import {
  NavController,
  LoadingController,
  AlertController,
  MenuController,
  PopoverController,
  ModalController,
} from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { Router } from '@angular/router';
import * as Parse from 'parse';

import * as Moment from 'moment';
import Calendars from 'datebook/src/utils/calendars.js';

import { Constants } from '../../models/constants';
import { EventSeriesInfo } from '../../models/event-series-info';
import {
  CountdownTimer,
  CountdownTimerDelegate,
} from '../../models/countdown-timer';
import { AppointmentTime } from '../../models/appointment-time';
import { Address } from '../../models/address';

import { EnterAddressPage } from '../enter-address/enter-address.page';
import { CostBreakdownPage } from '../cost-breakdown/cost-breakdown.page';

import { ApptCreator } from '../../services/appt-creation-service';
import { UserService, Customer } from '../../services/user-service';
import { Util } from '../../services/util';
import { Alert } from '../../services/alert';
import { PromoService, PromoActivation } from '../../services/promo-service';
import { NavParamsHistoryStore } from '../../services/nav-params-history-store';
import { AnalyticsService } from '../../services/analytics-service';
import { PaymentService } from '../../services/payment-service';
import { GiftCard, GiftCardsService } from '../../services/gift-cards-service';
import { MetaTagService } from '../../services/meta-tag-service';
import { ObservableService } from 'src/app/services/observable-service';
import { PopupPage } from '../popup/popup.page';

declare var Stripe: any;
declare var fbq: any;

@Component({
  selector: 'app-confirm-booking',
  templateUrl: './confirm-booking.page.html',
  styleUrls: ['./confirm-booking.page.scss'],
})
export class ConfirmBookingPage implements OnInit, CountdownTimerDelegate {
  cardFields: {
    cardholderName?: string;
  } = {};

  fields: {
    promoCode?: string;
  } = {};

  conciergeFields: {
    conciergeClientFullName?: string;
    conciergeClientEmail?: string;
    conciergeClientPhone?: string;
  } = {};

  stripe: any;
  card: any;

  timer: CountdownTimer;
  preventRejectOnLeave: boolean = false;

  // NAV FIELDS
  previousController?: string;

  selectedCustomer?: Customer;
  isBusinessConciergeBooking: boolean;

  paymentPickerOpen = false;
  addingNewPayment = false;
  confirming = false;
  confirmed = false;
  confirmedApptTime: AppointmentTime;
  confirmedServiceTypeListText: string;

  // UI Params
  apptAddressString: string = '';
  isCustomAddress = false;
  showAddUnitButton = false;

  promoCode?: string;

  constructor(
    private navCtrl: NavController,
    private navParamsHistoryStore: NavParamsHistoryStore,
    private loadingCtrl: LoadingController,
    private popoverCtrl: PopoverController,
    private modalCtrl: ModalController,
    private alert: Alert,
    private observables: ObservableService,
    private promoService: PromoService,
    private alertCtrl: AlertController,
    private analytics: AnalyticsService,
    private router: Router,
    private changeRef: ChangeDetectorRef,
    private menuCtrl: MenuController,
    private paymentService: PaymentService,
    private storage: Storage,
    public userService: UserService,
    public apptCreator: ApptCreator,
    private metaTagService: MetaTagService,
  ) {
    if (Constants.DEV_MODE) {
      this.stripe = Stripe(Constants.DEV_STRIPE_KEY);
    } else {
      this.stripe = Stripe(Constants.STRIPE_KEY);
    }

    if (
      this.router.getCurrentNavigation() &&
      this.router.getCurrentNavigation().extras.state
    ) {
      // We came from nav push. Load in initial props.
      const { state: navState } = this.router.getCurrentNavigation().extras;
      if (navState) {
        this.previousController = navState.previousController;
      }
    } else {
      this.apptCreator.rejectAppt();
      this.navCtrl.navigateBack('/explore');
      return;
    }

    if (this.apptCreator.selectedServiceTypeList) {
      this.apptCreator.selectedServiceTypeList.refreshText();
    }
  }

  ngOnInit() {
    this.changeRef.detectChanges();
  }

  ngAfterViewInit() {
    this.observables.publishMinimizeAllChats();

    this.isBusinessConciergeBooking = this.userService.isBusinessConcierge();

    if (this.isBusinessConciergeBooking) {
      console.log('business concierge - show single-use payment entry');
    } else {
      this.selectedCustomer = this.userService.user.customer;
    }

    this.analytics.track(AnalyticsService.ViewedConfirmBooking);
    this.analytics.track(AnalyticsService.ViewRequestApptConfirm);

    if (this.apptCreator.expiryDate) {
      const expiryTime = CountdownTimer.getTimerMinSec(
        this.apptCreator.expiryDate,
      );
      this.timer = new CountdownTimer(expiryTime.minLeft, expiryTime.secLeft);
    } else {
      let countdownMin = 5;
      if (this.userService.user.customer && !this.userService.isConcierge()) {
        // User has payment added. Give them a shorter countdown timer.
        countdownMin = 3;
      }
      this.timer = new CountdownTimer(countdownMin, 0);
    }

    this.timer.delegate = this;
    this.timer.start();

    this.promoService.getStoredCoupon((promo: string) => {
      if (promo) {
        this.promoCode = promo;
        this.apptCreator.coupon = promo;
        this.updateCost();
      }
    });
  }

  ionViewWillEnter() {
    this.updateUIParams();
    this.menuCtrl.enable(false);
  }

  ionViewDidEnter() {
    this.setupStripe('card-element');
    this.metaTagService.updateTitle(`Confirm Booking`);

    if (!this.userService.user.approvedCovidGuidelinesDate) {
      Parse.Config.get().then(async (config) => {
        const showInHomeCovidGuidelines = config.get(
          'showInHomeCovidGuidelines',
        );
        if (showInHomeCovidGuidelines) {
          let popupModal = await this.modalCtrl.create({
            component: PopupPage,
            componentProps: {
              showAcceptDecline: true,
              isCovidGuideline: true,
            },
            backdropDismiss: false,
            cssClass: 'auto-height',
          });

          popupModal.onDidDismiss().then((val: any) => {
            if (val.data && val.data.accepted) {
              this.userService.update(
                { approvedCovidGuidelinesDate: new Date() },
                () => {},
                () => {},
              );
            } else {
              this.closeAndReject();
            }
          });

          popupModal.present();
        }
      });
    }
  }

  ionViewWillLeave() {
    if (!this.preventRejectOnLeave) {
      this.timer.pause();
      this.apptCreator.rejectAppt();
    }
    this.preventRejectOnLeave = false;
  }

  private setupStripe(cardElementID: string) {
    if (!document.getElementById(cardElementID)) {
      console.log('no card element');
      return;
    }

    let elements = this.stripe.elements();
    var style = {
      base: {
        color: '#25282a',
        lineHeight: '50px',
        fontFamily: '"Fira Sans", Arial, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '14px',
        '::placeholder': {
          color: '#86878e',
        },
        border: '1px solid grey',
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    };

    this.card = elements.create('card', { style: style });

    this.card.mount('#' + cardElementID);

    this.card.addEventListener('change', (event: any) => {
      var displayError = document.getElementById('card-errors');
      if (event.error) {
        displayError.textContent = event.error.message;
      } else {
        displayError.textContent = '';
      }
    });
  }

  closeAndReject() {
    this.timer.pause();
    this.preventRejectOnLeave = false;
    this.navCtrl.navigateBack(this.previousController || '/explore');
  }

  backButtonAction() {
    this.closeAndReject();
  }

  countdownEnded() {
    this.alert.show(
      'Order Canceled',
      'Your reservation time expired and this order has been cancelled.',
      'Ok',
      () => {
        this.closeAndReject();
      },
    );
  }

  updateUIParams() {
    let address = this.apptCreator.address;
    this.isCustomAddress = false;
    if (this.apptCreator.customAddressData) {
      address = this.apptCreator.customAddressData.address;
      this.isCustomAddress = true;
    }

    this.apptAddressString = Util.formatShortAddress(address);

    this.updateAddUnitLabel();
  }

  // -- Payment options --

  selectedPaymentCardText() {
    if (this.selectedCustomer) {
      return this.paymentCardText(this.selectedCustomer);
    } else {
      return 'No payment selected';
    }
  }

  paymentCardText(customer: any) {
    if (customer.cardBrand) {
      return `${customer.cardBrand} ending *${customer.last4}`;
    } else {
      return `Card ending ${customer.last4}`;
    }
  }

  editPayment() {
    this.paymentPickerOpen = true;
    this.changeRef.detectChanges();
  }

  selectCustomer(customer: any) {
    this.closePaymentPicker();
    this.selectedCustomer = customer;
    this.changeRef.detectChanges();
  }

  closePaymentPicker() {
    this.paymentPickerOpen = false;
    this.changeRef.detectChanges();
  }

  addNewPayment() {
    this.closePaymentPicker();
    this.addingNewPayment = true;
    this.changeRef.detectChanges();
    this.setupStripe('card-element');
  }

  returnToCheckout() {
    this.addingNewPayment = false;
    this.changeRef.detectChanges();
  }

  async saveNewPayment() {
    this.addCard(
      () => {
        this.selectedCustomer = this.userService.user.customer;
        this.returnToCheckout();
        this.changeRef.detectChanges();
      },
      (errMsg: string) => {
        this.error(errMsg);
      },
    );
  }

  async addCard(success: () => void, error: (errMsg: string) => void) {
    if (!this.cardFields.cardholderName) {
      this.error('Please enter cardholder name.');
      return;
    }

    let loading = await this.loadingCtrl.create();
    loading.present();

    let data = {
      name: this.cardFields.cardholderName,
    };

    this.stripe.createToken(this.card, data).then((result: any) => {
      if (result.error) {
        loading.dismiss();
        var errorElement = document.getElementById('card-errors');
        errorElement.textContent = result.error.message;
      } else if (this.isBusinessConciergeBooking) {
        this.paymentService.createConciergeCustomer(
          result.token,
          this.cardFields.cardholderName,

          (customer: Customer) => {
            loading.dismiss();
            this.selectedCustomer = customer;
            this.changeRef.detectChanges();
            success();
          },
          (errorMsg: string) => {
            loading.dismiss();
            error(errorMsg);
          },
        );
      } else {
        this.paymentService.createCustomer(
          result.token,
          this.cardFields.cardholderName,

          () => {
            loading.dismiss();
            success();
          },
          (errorMsg: string) => {
            loading.dismiss();
            error(errorMsg);
          },
        );
      }
    });
  }

  customerSelected(customer: any) {
    return this.selectedCustomer && this.selectedCustomer.id == customer.id;
  }

  // -- end payment options --

  async openAddress(event: any) {
    if (this.isCustomAddress) return;

    const addressModal = await this.popoverCtrl.create({
      component: EnterAddressPage,
      event: event,
      componentProps: {},
      cssClass: ['SCWEB-popover', 'SCWEB-popover-address'],
    });

    addressModal.onDidDismiss().then((val: any) => {
      if (val && val.data && val.data.address) {
        this.apptCreator.address = val.data.address;
        this.updateUIParams();
        this.changeRef.detectChanges();

        this.analytics.track(AnalyticsService.EnteredAddress_ConfirmBooking);
        const fromGPS = val.data.isFromGPS;
        this.storage.set(Constants.ADDRESS_IS_FROM_GPS, fromGPS);
        this.userService.update(
          {
            address: val.data.address,
          },
          () => {
            this.userService.fetch(() => {});
          },
          (errorMsg) => {},
        );
      }
    });

    await addressModal.present();
  }

  updateAddUnitLabel() {
    if (
      (!this.apptCreator.address.unit || this.apptCreator.address.unit == '') &&
      !this.apptCreator.customAddressData
    ) {
      this.showAddUnitButton = true;
    } else {
      this.showAddUnitButton = false;
    }
  }

  async addUnitPressed() {
    let alert = await this.alertCtrl.create({
      cssClass: 'SC-caps',
      inputs: [
        {
          name: 'unit',
          placeholder: 'Enter unit',
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Save',
          handler: (data) => {
            if (!data) {
              return;
            }

            var unit = data.unit;

            if (unit) {
              this.apptCreator.address.unit = unit;
              this.updateUIParams();
              this.changeRef.detectChanges();
              this.userService.update(
                {
                  address: this.apptCreator.address,
                },
                () => {},
                (errorMsg) => {},
              );
            }
          },
        },
      ],
    });
    alert.present();
  }

  confirmPressed() {
    this.analytics.track(AnalyticsService.ConfirmPressed);
    this.timer.pause();

    if (!this.selectedCustomer) {
      this.addCard(
        () => {
          this.reserveAppt();
        },
        (errMsg: string) => {
          this.timer.start();
          this.error(errMsg);
        },
      );
      return;
    }

    this.reserveAppt();
  }

  reserveAppt() {
    this.timer.pause();

    if (this.confirming) {
      return;
    }

    this.confirming = true;
    this.changeRef.detectChanges();

    let customer;
    if (this.isBusinessConciergeBooking) {
      customer = this.selectedCustomer;
    } else {
      customer = this.userService.user.customer;
    }

    this.apptCreator.reserveAppt(
      customer,
      this.conciergeFields.conciergeClientFullName,
      this.conciergeFields.conciergeClientEmail,
      this.conciergeFields.conciergeClientPhone,
      (apptId) => {
        let analyticsProps: any = {
          type: this.userService.user.currentProType || 'Hair',
          value: this.apptCreator.cost.finalCost || 0,
          currency: this.apptCreator.cost.currency || 'USD',
          id: apptId,
          revenue: this.apptCreator.cost.finalCost || 0,
        };

        this.analytics.track(AnalyticsService.BookingComplete, analyticsProps);

        fbq('track', 'Purchase', {
          currency: this.apptCreator.cost.currency || 'USD',
          value: this.apptCreator.cost.finalCost || 0,
          eventID: apptId,
          event: 'Purchase',
        });

        this.confirmedApptTime = this.apptCreator.apptTime;
        this.confirmedServiceTypeListText =
          this.apptCreator.selectedServiceTypeList.longText;

        this.observables.publishBooked();
        this.clearPromoCodeFromStorage();
        this.preventRejectOnLeave = true;
        this.confirmed = true;
        this.changeRef.detectChanges();
      },
      (msg) => {
        this.confirming = false;
        this.changeRef.detectChanges();
        this.timer.start();
        this.alert.show('Error', msg);
      },
    );
  }

  viewUpcoming() {
    this.preventRejectOnLeave = true;
    this.navCtrl.navigateRoot('/appointments');
  }

  // async reschedulePressed() {
  //   if (!this.selectedCustomer) {
  //     this.preventRejectOnLeave = true;
  //     this.navCtrl.navigateForward('/payment');
  //     return;
  //   }

  //   let loading = await this.loadingCtrl.create();
  //   loading.present();
  //   this.timer.pause();

  //   this.apptCreator.rescheduleAppt(this.userService.user.customer.id, this.apptID, (apptID: string) => {

  //     loading.dismiss();
  //     if (apptID) {
  //       this.clearPromoCodeFromStorage()
  //       this.closeAndReject();
  //     } else {
  //       this.alert.show('Error', 'Something went wrong. Please try again.');
  //     }

  //   }, (msg) => {
  //     loading.dismiss();
  //     this.timer.start();
  //     this.alert.show('Error', msg);
  //   })
  // }

  async applyPromoCode() {
    if (!this.fields.promoCode) return;

    let promo = this.fields.promoCode.toUpperCase();

    let loading = await this.loadingCtrl.create();
    loading.present();

    this.promoService.checkPromoValidity(
      promo,
      (
        barberID?: string,
        barberLink?: string,
        activation?: PromoActivation,
        eventRedirectionData?: EventSeriesInfo[],
        giftCardData?: GiftCard,
      ) => {
        if (giftCardData) {
          loading.dismiss();

          this.alert.show(
            'Redeem gift card?',
            `Code: ${giftCardData.hyphenatedCode}`,
            'Redeem',
            async () => {
              let loading = await this.loadingCtrl.create();
              loading.present();

              GiftCardsService.redeemGiftCard(
                giftCardData.code,
                (giftCard: GiftCard) => {
                  loading.dismiss();
                  this.alert.show(
                    'Success!',
                    `${Util.formatCost(
                      giftCard.amount,
                      this.userService.user.currency,
                    )} has been credited to your account and will be applied automatically to your next order.`,
                  );
                  this.observables.publishUpdateUser();
                  this.updateCost();
                },
                (errorMsg: string) => {
                  loading.dismiss();
                  this.error(errorMsg);
                },
              );
            },
            'Cancel',
            () => {},
          );
        } else {
          this.promoCode = promo;
          this.apptCreator.coupon = promo;
          this.updateCost(loading);
        }
      },
      (errMsg: string) => {
        loading.dismiss();
        this.error(errMsg);
      },
    );
  }

  updateCost(loading?: any) {
    this.apptCreator.calculateCost(
      (cost) => {
        if (loading) {
          loading.dismiss();
        }
        this.apptCreator.cost = cost;
        this.changeRef.detectChanges();
      },
      (errMsg: string) => {
        if (loading) {
          loading.dismiss();
        }
        this.error(errMsg);
      },
    );
  }

  removePromo() {
    this.promoCode = undefined;
    this.apptCreator.coupon = null;
    this.changeRef.detectChanges();
    this.clearPromoCodeFromStorage();
    this.updateCost();
  }

  async goToCostBreakdown() {
    const costBreakdownModal = await this.modalCtrl.create({
      component: CostBreakdownPage,
      componentProps: {
        cost: this.apptCreator.cost,
        selectedServiceTypeList: this.apptCreator.selectedServiceTypeList,
        promoCode: this.promoCode,
      },
      cssClass: 'auto-height',
    });

    await costBreakdownModal.present();
  }

  async openCalendarPicker() {
    const barberName = this.apptCreator.barber.fullName;
    const startDate = this.confirmedApptTime.date;
    const address = this.apptAddressString;

    const startMoment = Moment(startDate);
    const startString = startMoment.format('YYYY-MM-DDTHH:mm:ss.sssZ');
    const endString = startMoment
      .add(1, 'hour')
      .format('YYYY-MM-DDTHH:mm:ss.sssZ');

    const opts = {
      title: `Shortcut appointment with ${barberName}`,
      location: address,
      start: startString,
      end: endString,
    };

    let buttons = [
      {
        text: 'Google Calendar',
        handler: () => {
          window.open(Calendars.getGoogleCalendarUrl(opts), '_blank');
        },
      },
      {
        text: 'iCalendar',
        handler: () => {
          Calendars.downloadIcs(opts);
        },
      },
      {
        text: 'Outlook',
        handler: () => {
          window.open(Calendars.getMicrosoftCalendarUrl(opts), '_blank');
        },
      },
    ];

    let alert = await this.alertCtrl.create({
      header: 'Add To Calendar',
      buttons: buttons,
    });

    alert.present();
  }

  // Helpers

  timeText(apptTime: AppointmentTime) {
    let date = apptTime.date;
    let timezone = apptTime.timezone;
    // console.log(apptTime)
    // console.log(date)
    return `${Util.prettyTimeAdjusted(date, timezone)} on ${Util.dayMonthDate(
      date,
      timezone,
    )}`;
  }

  roundedRating(n: number) {
    return Math.round(n);
  }

  toRange(n: number) {
    return Util.toRange(n);
  }

  updateTimer() {
    console.log('update timer');
    this.changeRef.detectChanges();
  }

  timerSecLeft(sec: number) {
    if (this.timer.minLeft < 1 && this.timer.secLeft <= sec) {
      return true;
    }
    return false;
  }

  formatCost(num: number, currency: string) {
    return Util.formatCost(num, currency);
  }

  private error(msg: string) {
    this.alert.show('Error', msg);
  }

  private clearPromoCodeFromStorage() {
    this.promoService.clearCoupon();
    this.promoCode = undefined;
    this.apptCreator.coupon = null;
  }
}
